/**
 * What colour is at a point in the design. For the eyedropper.
 *
 * The answer is the ink the design says, not the pixel on the screen. The two
 * differ: the screen shows ink through the naive conversion, and a sampled
 * pixel converted back again lands a few per cent off in every channel - so the
 * logo's red, picked and reused, would print as a slightly different red beside
 * the original. Reading the document instead gives back 0/90/86/12 exactly.
 *
 * A photograph has no ink of its own until the press makes some, so for a
 * picture the caller falls back to the pixel.
 */

import type { Colour, Gradient, Ink, Layer } from '../model/types';
import { isGradient } from '../model/types';
import { shapeDef } from '../shapes/library';
import { pageToLocal, type Point } from '../shapes/nodes';

/** The colour a layer puts at a point on the page, or null when it has no ink of its own there. */
export function inkAt( layer: Layer, point: Point ): Colour | null {
  switch ( layer.type ) {
    case 'text':
      return isGradient( layer.fill ) ? layer.fill.stops[ 0 ]?.colour ?? null : layer.fill;
    case 'qr':
    case 'barcode':
      return layer.fill;
    case 'shape': {
      // A line is its stroke; so is a drawing with nothing in its fill, where
      // the only ink anyone could have clicked on is the outline.
      const emptyFill = ! isGradient( layer.fill ) && layer.fill.space === 'cmyk' && layer.fill.value.every( ( v ) => v === 0 );

      if ( layer.stroke && ( shapeDef( layer.shape ).strokeOnly || emptyFill ) ) return layer.stroke.colour;

      if ( ! isGradient( layer.fill ) ) return layer.fill;

      const local = pageToLocal( layer, point );

      return along( layer.fill, where( layer.fill, local.x / layer.w, local.y / layer.h ) );
    }
    default:
      return null;
  }
}

/**
 * How far along a gradient a point is, from its place in the shape's box.
 *
 * The same geometry as the SVG writer (press/svg.ts), which lays gradients out
 * in objectBoundingBox units - so the answer here is the colour the proof and
 * the press file put at that point, not an estimate of it.
 */
function where( gradient: Gradient, u: number, v: number ): number {
  if ( gradient.kind === 'radial' ) return Math.hypot( u - 0.5, v - 0.5 ) / 0.5;

  const a = ( gradient.angle * Math.PI ) / 180;

  return ( u - 0.5 ) * Math.cos( a ) + ( v - 0.5 ) * Math.sin( a ) + 0.5;
}

/**
 * The colour at a place along a gradient.
 *
 * Mixed ink by ink, which is what a DeviceCMYK shading does on press. Two stops
 * that are not both ink - a colour from an older design kept as a hex - are not
 * mixed, and the nearer one is given back as it is.
 */
export function along( gradient: Gradient, t: number ): Colour {
  const stops = gradient.stops.slice().sort( ( a, b ) => a.at - b.at );
  const first = stops[ 0 ];
  const last = stops[ stops.length - 1 ];

  if ( ! first || ! last ) return { space: 'cmyk', value: [ 0, 0, 0, 1 ] };

  const at = Math.min( 1, Math.max( 0, t ) );

  if ( at <= first.at ) return first.colour;
  if ( at >= last.at ) return last.colour;

  for ( let i = 1; i < stops.length; i++ ) {
    const from = stops[ i - 1 ];
    const to = stops[ i ];

    if ( ! from || ! to || at > to.at ) continue;

    const share = to.at === from.at ? 0 : ( at - from.at ) / ( to.at - from.at );
    const a = inkOf( from.colour );
    const b = inkOf( to.colour );

    if ( ! a || ! b ) return share < 0.5 ? from.colour : to.colour;

    return {
      space: 'cmyk',
      value: a.map( ( v, index ) => Math.round( ( v + ( ( b[ index ] ?? 0 ) - v ) * share ) * 1000 ) / 1000 ) as Ink,
    };
  }

  return last.colour;
}

function inkOf( colour: Colour ): Ink | null {
  if ( colour.space === 'cmyk' ) return colour.value;
  if ( colour.space === 'spot' ) return colour.fallback;

  return null;
}
